import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import PageBanner from './PageBanner';
import FeaturedProducts from '../FeaturedProducts';
import Products from '../Products';

const StateHighlightSection = styled.section`
  padding: 1rem 2rem 3rem 2rem;
  max-width: 1100px;
  margin: 0 auto;
`;

const HighlightCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  border: 3px solid #ff6b6b;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 1.8rem 1.2rem;
  }

  h2 {
    color: #ff6b6b;
    font-size: 1.8rem;
    margin: 0 0 1rem 0;
  }

  p {
    color: #444;
    font-size: 1.05rem;
    line-height: 1.6;
    margin: 0 0 1rem 0;
  }

  ul {
    padding-left: 1.2rem;
    margin: 0 0 1.5rem 0;
    color: #555;
    line-height: 1.8;
  }

  .shipping-box {
    background: #fdf5e6;
    border: 2px dashed #ffb142;
    border-radius: 16px;
    padding: 1.5rem;
    text-align: center;

    .icon {
      font-size: 2.5rem;
      margin-bottom: 0.5rem;
    }

    h3 {
      color: #d35400;
      margin: 0 0 0.5rem 0;
    }

    p {
      color: #666;
      font-size: 0.95rem;
      margin: 0;
    }
  }
`;

const stateConfigs = {
  texas: {
    state: 'Texas',
    badge: '🤠 Lone Star Flock',
    title: 'Chicken Apparel & Farm Gifts in Texas',
    subtitle: 'Everything is bigger in Texas — including our love for backyard flocks and poultry fashion! Proudly delivering to Houston, Austin, Dallas, San Antonio, and rural farm homesteads statewide.',
    headline: 'Proudly Serving Texas Chicken Keepers',
    body1: 'Whether you are caring for heritage hens in Hill Country, raising show roosters, or showing off your poultry attitude at the farmers market, Chikin Lady has your wardrobe covered.',
    body2: 'Our high-quality chicken bandanas, funny tees, swimwear, and accessories are designed to stand up to the Texas heat and keep you looking cluckin\' awesome.',
    highlights: [
      'Direct statewide delivery across all Texas counties',
      'Ultra-comfortable, breathable cotton apparel',
      'Perfect gifts for backyard chicken enthusiasts and homesteaders'
    ],
    deliveryNote: 'Fast Direct Shipping to Texas 🚚'
  },
  california: {
    state: 'California',
    badge: '🌴 West Coast Poultry',
    title: 'Chicken Apparel & Farm Gifts in California',
    subtitle: 'From the urban coops of Los Angeles, San Diego, and the Bay Area to the sprawling farms of the Central Valley, West Coast chicken lovers rock the coop in style.',
    headline: 'California Backyard Chicken Style',
    body1: 'California is home to one of the most passionate sustainable living and urban farming movements in the nation. Show off your crazy chicken lady status with vibrant, eco-conscious designs.',
    body2: 'Whether you need a sun-shielding chicken bandana, a head-turning chicken bikini for the coast, or quirky rooster mugs, we deliver fresh merchandise directly to your CA address.',
    highlights: [
      'Fast shipping across Los Angeles, Bay Area, San Diego, and Central Valley',
      'Sunny, vibrant designs inspired by coastal and farm lifestyle',
      'Unique boutique chicken fashion you won\'t find in standard stores'
    ],
    deliveryNote: 'Fast Direct Shipping to California ☀️'
  },
  florida: {
    state: 'Florida',
    badge: '☀️ Sunshine State Coops',
    title: 'Chicken Apparel & Farm Gifts in Florida',
    subtitle: 'Keep your flock styling in the Sunshine State! Vibrant chicken apparel, accessories, and gifts crafted for poultry enthusiasts in Miami, Orlando, Tampa, Jacksonville, and rural FL farms.',
    headline: 'Sunshine & Roosters in Florida',
    body1: 'Tropical weather calls for bright colors, breathable fabrics, and bold attitude! Chikin Lady brings cheerful, humorous poultry apparel to backyard flock owners all over Florida.',
    body2: 'From pool days in your custom chicken bikini to coop chores in our lightweight tees and bandanas, our gear brings guaranteed smiles to your neighborhood.',
    highlights: [
      'Reliable delivery to Orlando, Tampa, Miami, Jacksonville, and beyond',
      'Lightweight, high-quality gear perfect for humid summer days',
      'Great conversation starters for farm lovers and gift givers'
    ],
    deliveryNote: 'Fast Direct Shipping to Florida 🌴'
  }
};

const StateStorePage = ({ stateKey }) => {
  const config = stateConfigs[stateKey] || stateConfigs.texas;

  useEffect(() => {
    document.title = `${config.title} | Chikin Lady Delivery`;
  }, [config]);

  return (
    <>
      <PageBanner
        badge={config.badge}
        title={config.title}
        subtitle={config.subtitle}
        primaryText="🛒 Shop Featured Items"
        primaryLink="#products"
        secondaryText="🛍️ Store Hub"
        secondaryLink="/chikin-lady-shopping"
      />

      <StateHighlightSection>
        <HighlightCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div>
            <h2>{config.headline}</h2>
            <p>{config.body1}</p>
            <p>{config.body2}</p>
            <ul>
              {config.highlights.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
          <div className="shipping-box">
            <div className="icon">📦🐔</div>
            <h3>{config.deliveryNote}</h3>
            <p>
              All products ship straight to your door with tracking. 
              Browse our catalog below or visit our direct stores for special orders!
            </p>
          </div>
        </HighlightCard>
      </StateHighlightSection>

      <FeaturedProducts />
      <Products />
    </>
  );
};

export default StateStorePage;
